import httpClient from "../httpClient";

// ===== INTERFACES =====

export interface Location {
  id: number;
  name: string;
  city: string;
  state?: string;
  country?: string;
  address: string;
  zipCode?: string;
  email?: string;
  phone?: string;
  capacity: number;
  rooms?: number;
  timezone?: string;
  manager?: string;
  facilities: string[];
  status: string;
}

export interface Room {
  id: number;
  name: string;
  roomNumber: string;
  location: string;
  locationId: number;
  floor?: string;
  type: string;
  capacity: number;
  area?: string;
  description?: string;
  amenities: string[];
  status: string;
}

export interface Trainer {
  id: number;
  userId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  bio?: string;
  rating?: number;
  sessions?: number;
  expertise: string[];
  certifications: string[];
  experience: number;
  rate: number;
  availability: string;
  languages: string[];
  status: string;
  initials?: string;
  color?: string;
}

export interface ILTStats {
  totalLocations: number;
  availableRooms: number;
  activeTrainers: number;
  totalCapacity: number;
}

// ===== API ENDPOINTS =====
const ENDPOINTS = {
  LOCATIONS: "/admin/ilt/locations",
  ROOMS: "/admin/ilt/rooms",
  TRAINERS: "/admin/ilt/trainers",
  STATS: "/admin/ilt/stats",
  AVAILABLE_INSTRUCTORS: "/admin/ilt/available-instructors",
  TRAINERS_FROM_USER: "/admin/ilt/trainers/from-user",
};

// ===== LOCATION SERVICES =====

export const getLocations = async (
  search?: string,
  status?: string
): Promise<{
  success: boolean;
  data?: Location[];
  error?: { message: string } | null;
}> => {
  try {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    if (status) params.append("status", status);

    const url = `${ENDPOINTS.LOCATIONS}${
      params.toString() ? `?${params.toString()}` : ""
    }`;
    const response = await httpClient.get<{
      success: boolean;
      data: Location[];
    }>(url);

    return {
      success: response.success,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? "Failed to fetch locations" },
    };
  }
};

export const getLocationById = async (
  id: number
): Promise<{
  success: boolean;
  data?: { location: Location; rooms: Room[] };
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<{
      success: boolean;
      data: { location: Location; rooms: Room[] };
    }>(`${ENDPOINTS.LOCATIONS}/${id}`);

    return {
      success: response.success,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? "Failed to fetch location" },
    };
  }
};

export const createLocation = async (
  locationData: Partial<Location>
): Promise<{
  success: boolean;
  data?: any;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<{
      success: boolean;
      data: any;
      message: string;
    }>(ENDPOINTS.LOCATIONS, locationData);

    return {
      success: response.success,
      data: response.data,
      message: response.message,
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? "Failed to create location" },
    };
  }
};

export const updateLocation = async (
  id: number,
  locationData: Partial<Location>
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.put<{
      success: boolean;
      message: string;
    }>(`${ENDPOINTS.LOCATIONS}/${id}`, locationData);

    return {
      success: response.success,
      message: response.message,
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? "Failed to update location" },
    };
  }
};

export const deleteLocation = async (
  id: number
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.delete<{
      success: boolean;
      message: string;
    }>(`${ENDPOINTS.LOCATIONS}/${id}`);

    return {
      success: response.success,
      message: response.message,
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? "Failed to delete location" },
    };
  }
};

// ===== ROOM SERVICES =====

export const getRooms = async (
  search?: string,
  status?: string,
  locationId?: number,
  type?: string
): Promise<{
  success: boolean;
  data?: Room[];
  error?: { message: string } | null;
}> => {
  try {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    if (status) params.append("status", status);
    if (locationId) params.append("locationId", locationId.toString());
    if (type) params.append("type", type);

    const url = `${ENDPOINTS.ROOMS}${
      params.toString() ? `?${params.toString()}` : ""
    }`;
    const response = await httpClient.get<{ success: boolean; data: Room[] }>(
      url
    );

    return {
      success: response.success,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? "Failed to fetch rooms" },
    };
  }
};

export const getRoomById = async (
  id: number
): Promise<{
  success: boolean;
  data?: Room;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<{ success: boolean; data: Room }>(
      `${ENDPOINTS.ROOMS}/${id}`
    );

    return {
      success: response.success,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? "Failed to fetch room" },
    };
  }
};

export const createRoom = async (
  roomData: Partial<Room>
): Promise<{
  success: boolean;
  data?: any;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<{
      success: boolean;
      data: any;
      message: string;
    }>(ENDPOINTS.ROOMS, roomData);

    return {
      success: response.success,
      data: response.data,
      message: response.message,
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? "Failed to create room" },
    };
  }
};

export const updateRoom = async (
  id: number,
  roomData: Partial<Room>
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.put<{
      success: boolean;
      message: string;
    }>(`${ENDPOINTS.ROOMS}/${id}`, roomData);

    return {
      success: response.success,
      message: response.message,
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? "Failed to update room" },
    };
  }
};

export const deleteRoom = async (
  id: number
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.delete<{
      success: boolean;
      message: string;
    }>(`${ENDPOINTS.ROOMS}/${id}`);

    return {
      success: response.success,
      message: response.message,
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? "Failed to delete room" },
    };
  }
};

// ===== TRAINER SERVICES =====

export const getTrainers = async (
  search?: string,
  status?: string,
  availability?: string
): Promise<{
  success: boolean;
  data?: Trainer[];
  error?: { message: string } | null;
}> => {
  try {
    const params = new URLSearchParams();
    if (search) params.append("search", search);
    if (status) params.append("status", status);
    if (availability) params.append("availability", availability);

    const url = `${ENDPOINTS.TRAINERS}${
      params.toString() ? `?${params.toString()}` : ""
    }`;
    const response = await httpClient.get<{
      success: boolean;
      data: Trainer[];
    }>(url);

    return {
      success: response.success,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? "Failed to fetch trainers" },
    };
  }
};

export const getTrainerById = async (
  id: number
): Promise<{
  success: boolean;
  data?: Trainer;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<{ success: boolean; data: Trainer }>(
      `${ENDPOINTS.TRAINERS}/${id}`
    );

    return {
      success: response.success,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? "Failed to fetch trainer" },
    };
  }
};

export const createTrainer = async (
  trainerData: Partial<Trainer>
): Promise<{
  success: boolean;
  data?: any;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<{
      success: boolean;
      data: any;
      message: string;
    }>(ENDPOINTS.TRAINERS, trainerData);

    return {
      success: response.success,
      data: response.data,
      message: response.message,
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? "Failed to create trainer" },
    };
  }
};

export const updateTrainer = async (
  id: number,
  trainerData: Partial<Trainer>
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.put<{
      success: boolean;
      message: string;
    }>(`${ENDPOINTS.TRAINERS}/${id}`, trainerData);

    return {
      success: response.success,
      message: response.message,
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? "Failed to update trainer" },
    };
  }
};

export const deleteTrainer = async (
  id: number
): Promise<{
  success: boolean;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.delete<{
      success: boolean;
      message: string;
    }>(`${ENDPOINTS.TRAINERS}/${id}`);

    return {
      success: response.success,
      message: response.message,
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? "Failed to delete trainer" },
    };
  }
};

// ===== STATISTICS SERVICE =====

export const getILTStats = async (): Promise<{
  success: boolean;
  data?: ILTStats;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<{ success: boolean; data: ILTStats }>(
      ENDPOINTS.STATS
    );

    return {
      success: response.success,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? "Failed to fetch ILT stats" },
    };
  }
};

// ===== INSTRUCTOR MANAGEMENT SERVICES =====

export interface Instructor {
  uuid: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
}

export const getAvailableInstructors = async (): Promise<{
  success: boolean;
  data?: Instructor[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<{
      success: boolean;
      data: Instructor[];
    }>(ENDPOINTS.AVAILABLE_INSTRUCTORS);

    return {
      success: response.success,
      data: response.data,
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? "Failed to fetch available instructors" },
    };
  }
};

export const addTrainerFromUser = async (
  userId: string,
  trainerData?: Partial<Trainer>
): Promise<{
  success: boolean;
  data?: any;
  message?: string;
  error?: { message: string } | null;
}> => {
  try {
    const payload = {
      userId,
      ...trainerData,
    };

    const response = await httpClient.post<{
      success: boolean;
      data: any;
      message: string;
    }>(ENDPOINTS.TRAINERS_FROM_USER, payload);

    return {
      success: response.success,
      data: response.data,
      message: response.message,
      error: null,
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? "Failed to create trainer from user" },
    };
  }
};
